import { KeyboardEvent, ReactElement, useCallback } from 'react';

import KEY_CODES from '../../../../constants/KEY_CODES';

interface UseMenuKeyboardNavigationOptions {
  items?: ReactElement[];
  selectedItemIndex: number;
  handleSelected(newSelectedIndex: number): void;
  isOpened: boolean;
  handleToggle(): void;
  handleClose(): void;
  handleItemAction(event: KeyboardEvent<HTMLElement>): void;
}

const useMenuKeyboardNavigation = ({
  items, selectedItemIndex, handleSelected, handleToggle, handleClose, handleItemAction, isOpened,
}: UseMenuKeyboardNavigationOptions) => {
  const normalizeIndex = useCallback((index: number) => {
    if (index < 0) {
      return 0;
    }

    if (index > items.length - 1) {
      return items.length - 1;
    }

    return index;
  }, [items.length]);

  const selectItemByIndex = useCallback((event: KeyboardEvent<HTMLElement>, index: number) => {
    event.preventDefault();

    const normalizedIndex = normalizeIndex(index);

    if (handleSelected && normalizedIndex !== selectedItemIndex) {
      handleSelected(normalizedIndex);
    }
  }, [normalizeIndex, handleSelected, selectedItemIndex]);

  // eslint-disable-next-line consistent-return
  const handleKeyboardNavigation = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey && (event.keyCode === KEY_CODES.UP || event.keyCode === KEY_CODES.DOWN)) {
      handleToggle();
    } else if (isOpened) {
      if (event.keyCode === KEY_CODES.ENTER || event.keyCode === KEY_CODES.SPACE) {
        handleItemAction(event);
      } else if (event.keyCode === KEY_CODES.UP) {
        selectItemByIndex(event, selectedItemIndex - 1);
      } else if (event.keyCode === KEY_CODES.DOWN) {
        selectItemByIndex(event, selectedItemIndex + 1);
      } else if (event.keyCode === KEY_CODES.HOME || event.keyCode === KEY_CODES.PAGE_UP) {
        selectItemByIndex(event, 0);
      } else if (event.keyCode === KEY_CODES.END || event.keyCode === KEY_CODES.PAGE_DOWN) {
        selectItemByIndex(event, items.length - 1);
      } else if (event.keyCode === KEY_CODES.ESC) {
        handleClose();
      }
    }
  }, [handleToggle, isOpened, handleItemAction, selectItemByIndex, selectedItemIndex, items.length, handleClose]);

  return {
    handleKeyboardNavigation,
  };
};

export default useMenuKeyboardNavigation;
